/**
 * Fit math + labels for the control-plane rail heatmap.
 *
 * The rail is a fixed 300px column, but the width the heatmap actually gets
 * varies: panel borders, rail padding, and — the one that broke the hardcoded
 * 19-week grid — a classic (non-overlay) vertical scrollbar on the rail
 * stealing ~15px on macOS "always show scroll bars" / Windows. So the week
 * count is computed from the measured container width instead of assumed.
 *
 * Geometry (from ironvale's heatmap.css): a 30px day gutter, then the body
 * gap, then `weeks` cells with a gap between columns:
 *   width = GUTTER + GAP + weeks·CELL + (weeks−1)·GAP = GUTTER + weeks·(CELL+GAP)
 */

export const HEATMAP_CELL = 9
export const HEATMAP_GAP = 3
export const HEATMAP_GUTTER = 30
/** Widest grid the 300px rail can ever host (262px content → 19 columns). */
export const MAX_WEEKS = 19
/** Floor so very narrow phones still show a readable month, not two columns. */
export const MIN_WEEKS = 4

/** Widest whole number of week columns that fits `width` px, clamped to
 *  [MIN_WEEKS, MAX_WEEKS]. Unmeasured (0/NaN) widths get the maximum, so SSR
 *  or a pre-layout first pass never renders an over-shrunk grid. */
export function weeksForWidth(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return MAX_WEEKS
  const weeks = Math.floor((width - HEATMAP_GUTTER) / (HEATMAP_CELL + HEATMAP_GAP))
  return Math.min(MAX_WEEKS, Math.max(MIN_WEEKS, weeks))
}

/** Rendered width in px of a `weeks`-column grid (gutter included). */
export function heatmapWidth(weeks: number): number {
  return HEATMAP_GUTTER + weeks * (HEATMAP_CELL + HEATMAP_GAP)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-07-12`, 14 → `Jul 12 — 14 events` (UTC, matching the day buckets). */
export function formatDaySummary(date: string, count: number, unit = 'event'): string {
  const [, m, d] = date.split('-').map(Number)
  const label = m >= 1 && m <= 12 && d ? `${MONTHS[m - 1]} ${d}` : date
  return `${label} — ${count} ${unit}${count === 1 ? '' : 's'}`
}
